import { motion, useReducedMotion } from 'motion/react';
import { Children, Fragment, useId, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { ALIGN, GAP, JUSTIFY, sizingClass, WRAP_GAP } from '../tokens';

import './Row.css';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

// The container name every Row establishes for its own `stackBelow` query. It is not
// unique per instance: a container query always resolves against the *nearest* ancestor
// carrying this name, so a nested Row's query still lands on its own wrapper.
const CONTAINER_NAME = 'lb-row';

// A separator sits before every child but the first, and shows only between two
// children that rendered: a hidden child renders nothing, so its separator would
// otherwise lead, trail or double up
const SEPARATOR = 'data-row-separator';
const CONTENT = `:not(style):not([${SEPARATOR}])`;

// `sticky` (the mobile action bar hoist) has nothing to do on web; the prop is accepted
// so the same document renders on both platforms.
export const Row: ComponentFn<typeof catalog, 'Row'> = ({
  props,
  children,
}) => {
  const reducedMotion = useReducedMotion();
  const stackId = useId();
  const sizing = sizingClass(props.sizing);
  const target = `[data-stack-target="${stackId}"]`;
  const hideSeparators = `${target} > ${CONTENT} ~ [${SEPARATOR}]:has(+ ${CONTENT}) { display: none; }`;
  const separatorRules =
    props.separator !== undefined
      ? `${target} > [${SEPARATOR}] { display: none; } ${target} > ${CONTENT} ~ [${SEPARATOR}]:has(+ ${CONTENT}) { display: inline; }`
      : '';
  const stackRules =
    props.stackBelow !== undefined
      ? `@container ${CONTAINER_NAME} (max-width: ${props.stackBelow}px) { ${target} { flex-direction: column; }${props.separator !== undefined ? ` ${hideSeparators}` : ''} }`
      : '';
  const rules = [separatorRules, stackRules].filter(Boolean).join(' ');
  const style = rules ? <style>{rules}</style> : null;
  const wrapped = props.stackBelow !== undefined || props.fieldRow === true;
  let content: ReactNode = children;
  if (props.fieldRow) {
    const [label, ...values] = Children.toArray(children);
    // Row.css hides the row while its label is the only thing that rendered
    content = (
      <>
        <span data-field-label="" className="contents">
          {label}
        </span>
        {values}
      </>
    );
  } else if (props.separator !== undefined) {
    content = Children.toArray(children).map((child, index) => (
      <Fragment key={index}>
        {index > 0 ? (
          <span
            {...{ [SEPARATOR]: '' }}
            aria-hidden="true"
            className="text-foreground-muted"
          >
            {props.separator}
          </span>
        ) : null}
        {child}
      </Fragment>
    ));
  }
  const flexRow = (
    <motion.div
      layout
      transition={{ duration: reducedMotion ? 0 : 0.18 }}
      data-stack-target={rules ? stackId : undefined}
      className={cn(
        'card-row flex min-w-0',
        props.wrap && 'flex-wrap',
        sizing,
        (props.wrap ? WRAP_GAP : GAP)[props.gap ?? 'sm'],
        ALIGN[props.align ?? 'center'],
        JUSTIFY[props.justify ?? 'start']
      )}
    >
      {wrapped ? null : style}
      {content}
    </motion.div>
  );

  if (!wrapped) return flexRow;

  return (
    <div
      data-field-row={props.fieldRow ? '' : undefined}
      className={cn('min-w-0', sizing)}
      style={
        props.stackBelow === undefined
          ? undefined
          : { containerType: 'inline-size', containerName: CONTAINER_NAME }
      }
    >
      {style}
      {flexRow}
    </div>
  );
};
